/**
 * The two calls the reply composer makes: list the voices, stream a draft.
 *
 * ONE IMPLEMENTATION, NOT TWO. Both apps had their own copy of this, and the copies
 * had drifted onto different routes: the web app was on `/v1/communities/{id}/voices`
 * and `POST …/moderation/suggestions`, the Helper still on `/admin/moderation/voices`
 * and `/admin/moderation/suggest` with a flat `{skool_id, kind, id}` body. Same
 * feature, same frames, two request shapes and two SSE parsers to keep in step.
 *
 * The routes here are the v1 ones, and both credentials reach them: the device-token
 * scope table is gone, so a device token carries the user's own role and 403 now means
 * "not your community", not "not your token".
 *
 * WHAT THE HOST SUPPLIES is a way to send a request, and nothing else. The apps
 * differ in what they put on it — an admin session here, a 90-day device token
 * there — and in nothing about the frames that come back, so this file owns the
 * grammar and asks only for transport.
 */

import { readFrames } from '@api/sse';
import { api } from '@api/transport';

/** A staff member whose voice a suggestion can be drafted in. */
export interface VoiceOption {
  member_id: number;
  handle: string;
  name: string;
  role: string;
  picture_profile: string | null;
  /** The owner's own voice — selected when nothing has been picked before. */
  is_default: boolean;
}

/** One piece of indexed content the draft drew on, from the `done` frame. */
export interface SourceRef {
  title: string | null;
  course_title: string | null;
  url: string | null;
}

/**
 * An HTTP failure from either call.
 *
 * Carries the status so a host can act on it — the web app bounces to the login
 * page on 401 and to billing on 402, the Helper turns a 403 into a sentence about
 * its own scope. Hosts read `status` rather than the class, so neither app has to
 * import this to handle it.
 */
export class SuggestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'SuggestError';
    this.status = status;
    this.code = code;
  }
}

/**
 * `GET /v1/communities/{id}/voices?with_profile_only=true`.
 *
 * PAGINATED, where the legacy twin was not, so the page size is pinned to the
 * maximum: this list is a moderation team, and a voice missing from the picker
 * cannot be chosen at all.
 */
export async function fetchVoices(skoolId: number): Promise<VoiceOption[]> {
  const body = await api().get<{ items: Partial<VoiceOption>[] }>(
    `/v1/communities/${skoolId}/voices`,
    { with_profile_only: 'true', limit: 200 },
  );
  return (body.items ?? []).map((v) => ({
    member_id: v.member_id ?? 0,
    handle: v.handle ?? '',
    name: v.name ?? '',
    role: v.role ?? '',
    picture_profile: v.picture_profile ?? null,
    is_default: v.is_default ?? false,
  }));
}

/**
 * `POST /v1/communities/{id}/moderation/suggestions` — stream an AI-drafted reply,
 * in the community's voice and grounded in its indexed content.
 *
 * A POST-for-read, and the only one: it carries a body and it streams, which no GET
 * can do. It is also the one route here that SPENDS — three staged LLM calls — which
 * is why nothing retries.
 *
 * `onToken` fires per delta, `onSources` once on the `done` frame, and the promise
 * resolves when the stream ends. Throws a `SuggestError` on an HTTP failure or on
 * the inline `error` frame.
 */
export async function streamSuggestedReply(
  skoolId: number,
  kind: 'post' | 'comment',
  targetId: number,
  onToken: (delta: string) => void,
  opts: {
    voiceMemberId?: number | undefined;
    onSources?: ((sources: SourceRef[]) => void) | undefined;
    signal?: AbortSignal | undefined;
  } = {},
): Promise<void> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      target: { kind, id: targetId },
      ...(opts.voiceMemberId != null ? { voice_member_id: opts.voiceMemberId } : {}),
    }),
  };
  if (opts.signal) init.signal = opts.signal;

  let res: Response;
  try {
    res = await api().raw(`/v1/communities/${skoolId}/moderation/suggestions`, init);
  } catch (e) {
    // Status 0 is "never reached the server" — a dropped connection, not a refusal.
    throw new SuggestError(0, 'network_error', (e as Error)?.message || 'Network error.');
  }
  if (!res.ok) throw await httpError(res);
  // A 200 with no body is not a stream we can read, and resolving quietly would look
  // to the caller like a suggestion that generated nothing.
  if (!res.body) throw new SuggestError(0, 'no_stream', 'No response stream.');

  await readFrames(res.body, (event, data) => {
    if (event === 'token') {
      // `token` data is a JSON-encoded STRING, so a delta containing a newline or a
      // quote survives the frame grammar intact.
      onToken(JSON.parse(data) as string);
      return false;
    }
    if (event === 'done') {
      const d = JSON.parse(data) as { sources?: SourceRef[] };
      opts.onSources?.(d.sources ?? []);
      // Stop reading: `done` is the last frame that carries anything, and the caller
      // should not wait on the socket closing to render the result.
      return true;
    }
    if (event === 'error') {
      const d = JSON.parse(data) as { message?: string };
      // Status 0 again, for a different reason: the 200 was committed before the
      // model failed, so there is no HTTP status to report. A generation failure,
      // not an auth one — hosts must not treat it as a session problem.
      throw new SuggestError(0, 'suggestion_failed', d.message || 'Could not generate a suggestion.');
    }
    return false;
  });
}

/** Read the error envelope, whichever of the two shapes came back. */
async function httpError(res: Response): Promise<SuggestError> {
  const fallback = `${res.status} ${res.statusText}`;
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string };
      detail?: unknown;
    };
    if (body.error?.code) {
      return new SuggestError(res.status, body.error.code, body.error.message ?? fallback);
    }
    // Not the v1 shape. Anything answered before routing — a 405, an unknown path —
    // still comes back as `{"detail": …}`. Read it rather than losing the message.
    if (typeof body.detail === 'string') return new SuggestError(res.status, 'unknown_error', body.detail);
  } catch {
    /* not JSON — fall through to the status line */
  }
  return new SuggestError(res.status, 'unknown_error', fallback);
}


